import { SITE_URL, DEFAULT_OG_IMAGE, absoluteUrl, toEnPath, type Lang } from './routes';

const ORGANIZATION_NAME = 'QBP Solutions';
const ORGANIZATION_LEGAL_NAME = 'SAS QuelBonPlan';
const ORGANIZATION_LINKEDIN = 'https://www.linkedin.com/company/quelbonplan/';
const ORGANIZATION_TAX_ID = '88970627100026';

export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: ORGANIZATION_NAME,
  legalName: ORGANIZATION_LEGAL_NAME,
  url: SITE_URL,
  logo: DEFAULT_OG_IMAGE,
  address: {
    '@type': 'PostalAddress',
    streetAddress: '24 Avenue de Moka',
    addressLocality: 'Saint-Malo',
    postalCode: '35400',
    addressCountry: 'FR',
  },
  taxID: ORGANIZATION_TAX_ID,
  sameAs: [ORGANIZATION_LINKEDIN],
});

export const websiteSchema = (lang: Lang) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: ORGANIZATION_NAME,
  url: lang === 'fr' ? SITE_URL : `${SITE_URL}/en`,
  inLanguage: lang === 'fr' ? 'fr-FR' : 'en-US',
  publisher: { '@type': 'Organization', name: ORGANIZATION_NAME, url: SITE_URL },
});

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export const breadcrumbSchema = (items: BreadcrumbItem[]) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, idx) => ({
    '@type': 'ListItem',
    position: idx + 1,
    name: item.name,
    item: absoluteUrl(item.path),
  })),
});

export interface ArticleSchemaInput {
  title: string;
  description: string;
  date: string;
  author: string;
  thumbnail: string;
  slug: string;
}

export const articleSchema = (article: ArticleSchemaInput, lang: Lang) => {
  const path = lang === 'fr' ? `/blog/${article.slug}` : toEnPath(`/blog/${article.slug}`);
  const url = absoluteUrl(path);
  const image = article.thumbnail.startsWith('http')
    ? article.thumbnail
    : absoluteUrl(article.thumbnail);

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image,
    datePublished: article.date,
    dateModified: article.date,
    author: { '@type': 'Person', name: article.author },
    publisher: {
      '@type': 'Organization',
      name: ORGANIZATION_NAME,
      logo: { '@type': 'ImageObject', url: DEFAULT_OG_IMAGE },
    },
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    inLanguage: lang === 'fr' ? 'fr-FR' : 'en-US',
  };
};

interface PageLabel {
  fr: string;
  en: string;
  parent?: string;
}

const PAGE_LABELS: Record<string, PageLabel> = {
  '/solutions': { fr: 'Solutions', en: 'Solutions' },
  '/solutions/business-model': {
    fr: 'Modèle économique',
    en: 'Business Model',
    parent: '/solutions',
  },
  '/solutions/conformite': { fr: 'Conformité', en: 'Compliance', parent: '/solutions' },
  '/expertise': { fr: 'Expertise', en: 'Expertise' },
  '/references': { fr: 'Références', en: 'References' },
  '/about': { fr: 'À propos', en: 'About' },
  '/catalogue': { fr: 'Catalogue', en: 'Catalog' },
  '/contact': { fr: 'Contact', en: 'Contact' },
  '/status': { fr: 'Statut', en: 'Status' },
  '/blog': { fr: 'Blog', en: 'Blog' },
  '/buyback': { fr: 'Reprise', en: 'Buyback' },
  '/reprise-parc-informatique': {
    fr: 'Reprise parc informatique',
    en: 'IT Fleet Buyback',
  },
  '/mentions-legales': { fr: 'Mentions légales', en: 'Legal Notice' },
};

const HOME_LABEL: Record<Lang, string> = { fr: 'Accueil', en: 'Home' };

function normalizeToFrPath(path: string): string {
  if (!path.startsWith('/en')) return path;
  if (path === '/en') return '/';
  const stripped = path.replace(/^\/en/, '');
  const entry = Object.entries(PAGE_LABELS).find(
    ([fr]) => toEnPath(fr) === path
  );
  return entry ? entry[0] : stripped;
}

export function pageBreadcrumbItems(
  pathname: string,
  lang: Lang
): BreadcrumbItem[] | null {
  if (pathname === '/' || pathname === '/en') return null;
  if (/^\/(en\/)?blog\/[^/]+$/.test(pathname)) return null;

  const frPath = normalizeToFrPath(pathname);
  const label = PAGE_LABELS[frPath];
  if (!label) return null;

  const items: BreadcrumbItem[] = [
    { name: HOME_LABEL[lang], path: lang === 'fr' ? '/' : '/en' },
  ];

  if (label.parent) {
    const parentLabel = PAGE_LABELS[label.parent];
    if (parentLabel) {
      items.push({
        name: parentLabel[lang],
        path: lang === 'fr' ? label.parent : toEnPath(label.parent),
      });
    }
  }

  items.push({
    name: label[lang],
    path: lang === 'fr' ? frPath : toEnPath(frPath),
  });

  return items;
}

export function blogPostBreadcrumbItems(
  postTitle: string,
  postSlug: string,
  lang: Lang
): BreadcrumbItem[] {
  const blogPath = lang === 'fr' ? '/blog' : '/en/blog';
  const postPath = lang === 'fr' ? `/blog/${postSlug}` : `/en/blog/${postSlug}`;
  return [
    { name: HOME_LABEL[lang], path: lang === 'fr' ? '/' : '/en' },
    { name: PAGE_LABELS['/blog'][lang], path: blogPath },
    { name: postTitle, path: postPath },
  ];
}
